import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, Clock, Users, Heart, BadgeCheck, Car, Home, Backpack, Utensils, Plane } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { favoritesApi } from '@/api';
import { toast } from 'sonner';

// Custom Link that scrolls to top
const ScrollLink = ({ to, children, className, ...props }) => {
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    navigate(to);
  };
  return <a href={to} onClick={handleClick} className={className} {...props}>{children}</a>;
};

export const ExperienceCard = ({ experience, showFavorite = true }) => {
  const { i18n, t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  
  const title = i18n.language === 'en' && experience.title_en ? experience.title_en : experience.title;
  const mainImage = experience.images?.[0] || 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=800&q=80';

  useEffect(() => {
    if (isAuthenticated && showFavorite) {
      favoritesApi.check(experience.experience_id)
        .then(data => setIsFavorited(data.favorited))
        .catch(() => {});
    }
  }, [experience.experience_id, isAuthenticated, showFavorite]);

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error(i18n.language === 'fr' ? 'Connectez-vous pour ajouter aux favoris' : 'Login to add to favorites');
      return;
    }
    
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await favoritesApi.remove(experience.experience_id);
        setIsFavorited(false);
        toast.success(i18n.language === 'fr' ? 'Retiré des favoris' : 'Removed from favorites');
      } else {
        await favoritesApi.add(experience.experience_id);
        setIsFavorited(true);
        toast.success(i18n.language === 'fr' ? 'Ajouté aux favoris' : 'Added to favorites');
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setFavoriteLoading(false);
    }
  };

  return (
    <ScrollLink 
      to={`/experience/${experience.experience_id}`}
      className="block group"
      data-testid={`experience-card-${experience.experience_id}`}
    >
      <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img 
            src={mainImage} 
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          {/* Favorite Button */}
          {showFavorite && (
            <button
              onClick={handleFavoriteClick}
              disabled={favoriteLoading}
              className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isFavorited 
                  ? 'bg-primary text-white' 
                  : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-white hover:text-primary'
              }`}
              data-testid={`favorite-btn-${experience.experience_id}`}
            >
              <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            </button>
          )}
          
          {/* Rating Badge */}
          {experience.rating > 0 && (
            <div className="absolute top-3 left-3 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-semibold shadow-md">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span>{experience.rating.toFixed(1)}</span>
            </div>
          )}
          
          {/* Demo Badge */}
          {experience.explorer_id?.startsWith('demo_') && (
            <div className="absolute top-3 left-3 mt-10 bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded-full shadow-sm">
              {i18n.language === 'fr' ? 'Exemple' : 'Example'}
            </div>
          )}
          
          {/* Option Badges */}
          <div className="absolute bottom-3 left-3 flex gap-2">
            {experience.includes_vehicle && (
              <span className="bg-primary text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
                {i18n.language === 'fr' ? 'Véhicule' : 'Vehicle'}
              </span>
            )}
            {experience.includes_accommodation && (
              <span className="bg-orange-600 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
                {i18n.language === 'fr' ? 'Logement' : 'Accommodation'}
              </span>
            )}
          </div>
          
          {/* Guide Services Icons */}
          {experience.guide_service_types?.length > 0 && (
            <div className="absolute bottom-3 right-3 flex gap-1">
              {experience.guide_service_types.includes('vehicle') && (
                <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center shadow-md" title={i18n.language === 'fr' ? 'Véhicule disponible' : 'Vehicle available'}>
                  <Car className="w-4 h-4" />
                </div>
              )}
              {experience.guide_service_types.includes('accommodation') && (
                <div className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center shadow-md" title={i18n.language === 'fr' ? 'Hébergement disponible' : 'Accommodation available'}>
                  <Home className="w-4 h-4" />
                </div>
              )}
              {experience.guide_service_types.includes('equipment') && (
                <div className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-md" title={i18n.language === 'fr' ? 'Équipement disponible' : 'Equipment available'}>
                  <Backpack className="w-4 h-4" />
                </div>
              )}
              {experience.guide_service_types.includes('meal') && (
                <div className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center shadow-md" title={i18n.language === 'fr' ? 'Repas disponible' : 'Meals available'}>
                  <Utensils className="w-4 h-4" />
                </div>
              )}
              {experience.guide_service_types.includes('transfer') && (
                <div className="w-7 h-7 rounded-full bg-purple-500 text-white flex items-center justify-center shadow-md" title={i18n.language === 'fr' ? 'Transfert disponible' : 'Transfer available'}>
                  <Plane className="w-4 h-4" />
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-5">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{experience.location}</span>
          </div>
          
          <h3 className="font-bold text-lg text-gray-900 mb-3 group-hover:text-primary transition-colors line-clamp-2">
            {title}
          </h3>
          
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{experience.duration_hours}h</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{experience.max_participants} max</span>
            </div>
          </div>
          
          {/* Categories badges */}
          {experience.categories && experience.categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {experience.categories.slice(0, 3).map((cat) => (
                <span 
                  key={cat} 
                  className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                >
                  {cat}
                </span>
              ))}
              {experience.categories.length > 3 && (
                <span className="text-xs text-gray-400">
                  +{experience.categories.length - 3}
                </span>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              {experience.explorer_picture ? (
                <img 
                  src={experience.explorer_picture} 
                  alt={experience.explorer_name}
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-primary/20"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold text-sm">
                    {experience.explorer_name?.charAt(0)}
                  </span>
                </div>
              )}
              <span className="text-sm text-gray-600">{experience.explorer_name}</span>
              {experience.explorer_verified && (
                <BadgeCheck className="w-4 h-4 text-blue-500" title={i18n.language === 'fr' ? 'Guide vérifié' : 'Verified guide'} />
              )}
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-400">{t('exp.from')}</span>
              <span className="font-bold text-xl text-primary ml-1">{experience.price}€</span>
            </div>
          </div>
        </div>
      </div>
    </ScrollLink>
  );
};

export const ExperienceCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-md">
    <div className="aspect-[4/3] bg-gray-200 animate-pulse" />
    <div className="p-5 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
      <div className="h-6 bg-gray-200 rounded w-full animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
      <div className="flex justify-between pt-3 border-t border-gray-100">
        <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse" />
        <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse" />
      </div>
    </div>
  </div>
);
